import type { TooltipContentProps } from '@radix-ui/react-tooltip';
import { Content, TooltipTrigger, Tooltip as TooltipWrapper } from '@radix-ui/react-tooltip';
import { animated, config, useTransition } from '@react-spring/web';
import { isEmpty } from 'lodash-es';
import type { CSSProperties, JSX, PropsWithChildren } from 'react';
import { useState } from 'react';

type TooltipProps = {
  sideOffset?: TooltipContentProps['sideOffset'];
  side?: TooltipContentProps['side'];
  align?: TooltipContentProps['align'];
  alignOffset?: TooltipContentProps['alignOffset'];
  content: string | JSX.Element | undefined;
  ariaHidden?: boolean;
};

export const Tooltip = ({ children, content, sideOffset = 10, side, align, alignOffset, ariaHidden }: PropsWithChildren<TooltipProps>) => {
  const [open, setOpen] = useState(false);

  const transitions = useTransition(open, {
    from: { opacity: 0, ...toAxisProp(side, 10) },
    enter: { opacity: 1, ...toAxisProp(side, 0) },
    leave: { opacity: 0, ...toAxisProp(side, 10) },
    config: config.stiff,
  });

  if (isEmpty(content)) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  return (
    <TooltipWrapper onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        {children}
      </TooltipTrigger>

      {transitions((transitionStyle, item) => item ? (
        <Content sideOffset={sideOffset} side={side} align={align} alignOffset={alignOffset} className="z-50">
          <animated.div style={(transitionStyle as unknown as CSSProperties)} className="px-2 py-1 text-sm rounded shadow bg-contrast" aria-hidden={ariaHidden ?? true}>{content}</animated.div>
        </Content>
      ) : null)}
    </TooltipWrapper>
  );
};

const toAxisProp = (side: TooltipContentProps['side'] = 'bottom', value: number) => {
  const isHorizontal = (side === 'left' || side === 'right');
  const prop = isHorizontal ? 'x' : 'y';
  return {
    [prop]: (side === 'right' || side === 'bottom') ? (value * -1) : value,
  };
};

export default Tooltip;
